import { isCommercialList } from '@epicuro-next/features/engagement/list-utils';
import { List } from '@epicuro-next/models/engagement';
import { moveArrayElement, unique } from '@epicuro-next/utilities';
import { createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import { commercialListControlReducer } from './commercial-list-control.reducer';
import {
  applyCommercialListControl,
  changeListGroupingTypeInListEntitySuccess,
  changeProductOrderInListEntity,
  changeProductOrderInListEntitySuccess,
  loadList,
  loadNextListProducts,
  pushLoadedProductRefsToList,
  pushNewListProductsToListEntity,
  pushProductRefsToListAsOptimisticUI,
  removeProductFromListEntity,
  resetListEntities,
  resetListProducts,
  setOperableListId,
  setSearchSortTypeInListEntity,
  setUpdatingList,
  updateEntityList,
  upsertListEntity,
  upsertManyListEntities,
} from './list-entities.actions';
import {
  initialListEntityValues,
  ListEntitiesState,
  ListEntity,
} from './model';
import {
  areQueriesSame,
  getDefaultListProductsQuery,
  newQueryForNextProductsAction,
} from './utils';

export const adapter = createEntityAdapter<ListEntity>({
  selectId: ({ listId }) => listId,
});

export const initial: ListEntitiesState = adapter.getInitialState();

export const reducer = createReducer<ListEntitiesState>(
  initial,
  on(
    setOperableListId,
    (state, { listId }): ListEntitiesState => ({
      ...state,
      operableListId: listId,
    }),
  ),
  on(loadList, (state, { listId }): ListEntitiesState => {
    return adapter.updateOne(
      {
        id: listId,
        changes: {
          loading: true,
        },
      },
      state,
    );
  }),
  on(setUpdatingList, (state, { listId, isUpdating }): ListEntitiesState => {
    return adapter.updateOne(
      {
        id: listId,
        changes: {
          isUpdating,
        },
      },
      state,
    );
  }),
  on(
    upsertListEntity,
    (state, { listEntity }): ListEntitiesState =>
      adapter.upsertOne(
        {
          ...initialListEntityValues,
          ...listEntity,
          loading: listEntity.loading ?? false,
        },
        state,
      ),
  ),
  on(
    upsertManyListEntities,
    (state, { listEntities }): ListEntitiesState =>
      adapter.upsertMany(
        listEntities.map((item) => ({
          ...initialListEntityValues,
          ...item,
        })),
        state,
      ),
  ),
  on(updateEntityList, (state, { list }): ListEntitiesState => {
    const entity = state.entities[list?.id];

    return adapter.updateOne(
      {
        id: list?.id,
        changes: {
          ...entity,
          list,
        },
      },
      state,
    );
  }),
  on(
    setSearchSortTypeInListEntity,
    (state, { listId, sortType }): ListEntitiesState => {
      return adapter.updateOne(
        {
          id: listId,
          changes: {
            sortType,
          },
        },
        state,
      );
    },
  ),
  on(
    changeListGroupingTypeInListEntitySuccess,
    (state, { listId, groupingType }): ListEntitiesState => {
      return adapter.updateOne(
        {
          id: listId,
          changes: {
            list: {
              ...state.entities[listId]?.list,
              groupingType,
            } as List,
          },
        },
        state,
      );
    },
  ),
  on(loadNextListProducts, (state, action): ListEntitiesState => {
    const { listId } = action;
    const entity = state.entities[listId];
    const newQuery = newQueryForNextProductsAction(action, entity);
    const oldQuery = entity?.query;

    // check queries for regular lists
    // commercial lists don't have queries, they have references
    const queriesSame =
      !!entity?.list && isCommercialList(entity?.list)
        ? true
        : areQueriesSame(newQuery, oldQuery);

    const loadedProductIds = queriesSame ? entity?.loadedProductIds : [];
    const count = queriesSame ? entity?.count : 0;
    const loadingProducts =
      count && loadedProductIds ? count > loadedProductIds.length : true;

    return adapter.updateOne(
      {
        id: listId,
        changes: {
          loadingProducts,
          loadedProductIds,
          count,
        },
      },
      state,
    );
  }),
  on(
    pushLoadedProductRefsToList,
    (state, { listId, productIds, query, count }): ListEntitiesState => {
      const entity = state.entities[listId];
      return adapter.updateOne(
        {
          id: listId,
          changes: {
            query: query ?? entity?.query,
            count,
            loadingProducts: false,
            loadedProductIds: [
              ...(state.entities[listId]?.loadedProductIds ?? []),
              ...productIds,
            ].filter(unique),
          },
        },
        state,
      );
    },
  ),
  on(
    pushProductRefsToListAsOptimisticUI,
    (state, { listId, productIds, addToTop }): ListEntitiesState => {
      const listEntity = state.entities[listId];
      if (!listEntity) return state;

      const existingProductIds = listEntity?.loadedProductIds ?? [];

      return adapter.updateOne(
        {
          id: listId,
          changes: {
            loadingProducts: false,
            loadedProductIds: addToTop
              ? [...productIds, ...existingProductIds]
              : [...existingProductIds, ...productIds],
            optimisticalyAddedProductIds: [
              ...(listEntity.optimisticalyAddedProductIds ?? []),
              ...productIds,
            ].filter(unique),
          },
        },
        state,
      );
    },
  ),
  on(
    pushNewListProductsToListEntity,
    (state, { products, listId }): ListEntitiesState => {
      const entity = state.entities[listId];
      const newProductIds = products.map(({ productId }) => productId);
      const loadedProductIds = entity?.loadedProductIds ?? [];
      const allProductsIds = [...loadedProductIds, ...newProductIds].filter(
        unique,
      );

      return adapter.updateOne(
        {
          id: listId,
          changes: {
            count: allProductsIds.length,
            loadingProducts: false,
            loadedProductIds: [
              ...(entity?.loadedProductIds ?? []),
              ...newProductIds,
            ].filter(unique),
          },
        },
        state,
      );
    },
  ),
  on(
    resetListProducts,
    (state, { listId }): ListEntitiesState =>
      adapter.updateOne(
        {
          id: listId,
          changes: {
            query: {
              ...getDefaultListProductsQuery(listId),
              ...state.entities[listId]?.query,
              start: 0,
            },
            loadedProductIds: [],
            optimisticalyAddedProductIds: [],
            count: undefined,
          },
        },
        state,
      ),
  ),
  on(
    removeProductFromListEntity,
    (state, { productId, listId }): ListEntitiesState => {
      const isProductExist = state.entities[
        listId
      ]?.list?.productsReference?.some((p) => p.productId === productId);

      if (!isProductExist) {
        return state;
      }
      return adapter.updateOne(
        {
          id: listId,
          changes: {
            list: {
              ...state.entities[listId]?.list,
              amount: (state.entities[listId]?.list?.amount ?? 1) - 1,
              productsReference:
                state.entities[listId]?.list?.productsReference?.filter(
                  (item) => item.productId !== productId,
                ) ?? [],
              availableReferences:
                state.entities[listId]?.list?.availableReferences?.filter(
                  (item) => item.productId !== productId,
                ) ?? [],
              availableProductsCount:
                (state.entities[listId]?.list?.availableProductsCount ?? 1) - 1,
              unavailableProductsCount:
                (state.entities[listId]?.list?.unavailableProductsCount ?? 1) -
                0,
            } as List,
            loadedProductIds:
              state.entities[listId]?.loadedProductIds?.filter(
                (ids) => ids !== productId,
              ) ?? [],
            count: Math.max((state.entities[listId]?.count ?? 1) - 1, 0),
          },
        },
        state,
      );
    },
  ),
  on(
    applyCommercialListControl,
    (state, { listId, controlAction }): ListEntitiesState => {
      const entity = state.entities[listId];
      if (!entity) return state;

      const newEntity = commercialListControlReducer(entity, controlAction);
      return adapter.upsertOne(newEntity, state);
    },
  ),
  on(resetListEntities, () => adapter.getInitialState()),
  on(changeProductOrderInListEntity, (state, { listId }): ListEntitiesState => {
    const entity = state.entities[listId];

    return adapter.updateOne(
      {
        id: listId,
        changes: {
          reorderingCount: (entity?.reorderingCount ?? 0) + 1,
        },
      },
      state,
    );
  }),
  on(
    changeProductOrderInListEntitySuccess,
    (state, { listId, productId, order, brandId }): ListEntitiesState => {
      const entity = state.entities[listId];

      if (!entity || !entity.loadedProductIds) {
        return state;
      }

      const currentOrder = entity.loadedProductIds.indexOf(productId);

      if (currentOrder < 0) {
        return state;
      }

      const previousRefOrder = entity.list?.productsReference?.findIndex(
        (p) => p.brandId === brandId && p.productId === productId,
      ) as number;
      const previousAvailableRefOrder =
        entity.list?.availableReferences?.findIndex(
          (p) => p.brandId === brandId && p.productId === productId,
        ) as number;

      return adapter.updateOne(
        {
          id: listId,
          changes: {
            reorderingCount: (entity?.reorderingCount ?? 0) - 1,
            loadedProductIds: moveArrayElement(
              entity.loadedProductIds,
              currentOrder,
              order,
            ),
            list: {
              ...entity.list,
              productsReference: moveArrayElement(
                entity.list?.productsReference ?? [],
                previousRefOrder,
                order,
              ),
              availableReferences: moveArrayElement(
                entity.list?.availableReferences ?? [],
                previousAvailableRefOrder,
                order,
              ),
            } as List,
          },
        },
        state,
      );
    },
  ),
);
